import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Gift, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export function FloatingCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasShownExitIntent, setHasShownExitIntent] = useState(false);

  useEffect(() => {
    // Show floating CTA after scrolling
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = (scrolled / (documentHeight - windowHeight)) * 100;

      // Show after 20% scroll
      if (scrollPercentage > 20 && !isVisible) {
        setIsVisible(true);
      }
    };

    // Exit intent detection
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShownExitIntent && window.scrollY > 100) {
        setShowExitIntent(true);
        setHasShownExitIntent(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible, hasShownExitIntent]);

  if (!isVisible && !showExitIntent) return null;

  return (
    <>
      {/* Floating Button */}
      <div
        className={cn(
          "fixed bottom-8 right-8 z-40 transition-all duration-300",
          isMinimized ? "translate-y-20" : "translate-y-0",
          !isVisible && "opacity-0 pointer-events-none"
        )}
      >
        <div className="relative">
          {/* Pulsing Background */}
          <div className="absolute inset-0 bg-primary rounded-full blur-xl animate-pulse-glow" />
          
          {/* Main Button */}
          <Button
            size="lg"
            className="relative shadow-2xl hover:scale-105 transition-transform group"
            onClick={() => window.location.href = '#pricing'}
          >
            <Download className="mr-2 h-5 w-5 group-hover:animate-bounce" />
            Get SpeakFlow
            <span className="ml-2 text-xs opacity-75">50% OFF</span>
          </Button>

          {/* Minimize Button */}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="absolute -top-2 -right-2 h-6 w-6 bg-background border rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <X className="h-3 w-3" />
          </button>

          {/* Floating Badge */}
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap animate-bounce">
            Limited Time Offer!
          </div>
        </div>
      </div>

      {/* Exit Intent Modal */}
      {showExitIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowExitIntent(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-background rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-scale-in">
            {/* Close Button */}
            <button
              onClick={() => setShowExitIntent(false)}
              className="absolute top-4 right-4 p-2 hover:bg-accent rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Content */}
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
                <Gift className="h-8 w-8 text-primary" />
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl font-bold">Wait! Don't Miss Out</h2>
                <p className="text-xl text-primary font-semibold">
                  Get an Extra 10% OFF
                </p>
              </div>

              <p className="text-muted-foreground">
                You're just one step away from transforming your productivity. 
                As a special offer, here's an exclusive discount just for you!
              </p>

              {/* Countdown Timer */}
              <div className="flex items-center justify-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-orange-500" />
                <span className="text-orange-500 font-medium">
                  Offer expires in 10 minutes
                </span>
              </div>

              {/* Discount Code */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Your exclusive code:</p>
                <p className="text-2xl font-mono font-bold text-primary">SAVE10NOW</p>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Button 
                  size="lg" 
                  className="w-full shadow-lg"
                  onClick={() => {
                    setShowExitIntent(false);
                    window.location.href = '#pricing';
                  }}
                >
                  <Gift className="mr-2 h-5 w-5" />
                  Claim Your Discount
                </Button>
                <button
                  onClick={() => setShowExitIntent(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  No thanks, I'll pay full price
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}